/**
 * Edit Meal Dialog Component
 *
 * Portal-based dialog for editing meal name and nutrition values.
 * Features form validation, unsaved changes confirmation, and accessibility support.
 *
 * @module components/meal-plans/EditMealDialog
 */

import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/hooks/useTheme';
import { useMealActions } from '@/hooks/useMealActions';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { CloseButton } from '@/components/ui/CloseButton';
import type { MealPlanFrontend, EditMealFormData, EditMealFormErrors } from '@/types/mealPlan';
import { MAX_LENGTHS, VALUE_RANGES } from '@/types/mealPlan';

/**
 * Props for EditMealDialog component
 */
export interface EditMealDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** The meal data to edit */
  meal: MealPlanFrontend['mealPlanData']['meals'][0];
  /** ID of the meal plan containing this meal */
  mealPlanId: string;
  /** Optional success callback */
  onSuccess?: () => void;
}

/**
 * Edit Meal Dialog Component
 */
export const EditMealDialog: React.FC<EditMealDialogProps> = ({
  isOpen,
  onClose,
  meal,
  mealPlanId,
  onSuccess
}) => {
  const { isDark } = useTheme();
  const { updateMeal, isUpdating, error, resetError } = useMealActions(mealPlanId);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [formData, setFormData] = useState<EditMealFormData>({
    mealName: meal.mealName,
    nutrition: {
      caloriesPerServing: meal.nutrition.caloriesPerServing,
      proteinG: meal.nutrition.proteinG,
      carbsG: meal.nutrition.carbsG,
      fatG: meal.nutrition.fatG,
      fiberG: meal.nutrition.fiberG,
      sugarG: meal.nutrition.sugarG,
      sodiumMg: meal.nutrition.sodiumMg,
    }
  });

  const [errors, setErrors] = useState<EditMealFormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset form when meal changes or dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        mealName: meal.mealName,
        nutrition: {
          caloriesPerServing: meal.nutrition.caloriesPerServing,
          proteinG: meal.nutrition.proteinG,
          carbsG: meal.nutrition.carbsG,
          fatG: meal.nutrition.fatG,
          fiberG: meal.nutrition.fiberG,
          sugarG: meal.nutrition.sugarG,
          sodiumMg: meal.nutrition.sodiumMg,
        }
      });
      setErrors({});
      setTouched({});
      setHasChanges(false);
      resetError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, meal]);

  // Handle escape key and body scroll
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';

      // Focus first input after a short delay
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Check for changes
  useEffect(() => {
    const changed =
      formData.mealName !== meal.mealName ||
      formData.nutrition.caloriesPerServing !== meal.nutrition.caloriesPerServing ||
      formData.nutrition.proteinG !== meal.nutrition.proteinG ||
      formData.nutrition.carbsG !== meal.nutrition.carbsG ||
      formData.nutrition.fatG !== meal.nutrition.fatG ||
      formData.nutrition.fiberG !== meal.nutrition.fiberG ||
      formData.nutrition.sugarG !== meal.nutrition.sugarG ||
      formData.nutrition.sodiumMg !== meal.nutrition.sodiumMg;

    setHasChanges(changed);
  }, [formData, meal]);

  // Validation function
  const validateField = (name: string, value: string | number | undefined): string | undefined => {
    if (name === 'mealName') {
      const strValue = String(value).trim();
      if (!strValue) {
        return 'Meal name is required';
      }
      if (strValue.length > MAX_LENGTHS.MEAL_NAME) {
        return `Meal name must be ${MAX_LENGTHS.MEAL_NAME} characters or less`;
      }
      return undefined;
    }

    // For optional fields, empty is valid
    if (value === '' || value === undefined) {
      return undefined;
    }

    const numValue = Number(value);
    if (isNaN(numValue)) {
      return 'Must be a valid number';
    }

    switch (name) {
      case 'calories':
      case 'caloriesPerServing':
        if (numValue < VALUE_RANGES.CALORIES.min || numValue > VALUE_RANGES.CALORIES.max) {
          return `Calories must be between ${VALUE_RANGES.CALORIES.min} and ${VALUE_RANGES.CALORIES.max}`;
        }
        break;
      case 'protein':
      case 'proteinG':
        if (numValue < VALUE_RANGES.PROTEIN.min || numValue > VALUE_RANGES.PROTEIN.max) {
          return `Protein must be between ${VALUE_RANGES.PROTEIN.min} and ${VALUE_RANGES.PROTEIN.max}`;
        }
        break;
      case 'carbs':
      case 'carbsG':
        if (numValue < VALUE_RANGES.CARBS.min || numValue > VALUE_RANGES.CARBS.max) {
          return `Carbs must be between ${VALUE_RANGES.CARBS.min} and ${VALUE_RANGES.CARBS.max}`;
        }
        break;
      case 'fat':
      case 'fatG':
        if (numValue < VALUE_RANGES.FAT.min || numValue > VALUE_RANGES.FAT.max) {
          return `Fat must be between ${VALUE_RANGES.FAT.min} and ${VALUE_RANGES.FAT.max}`;
        }
        break;
      case 'fiber':
      case 'fiberG':
        if (numValue < VALUE_RANGES.FIBER.min || numValue > VALUE_RANGES.FIBER.max) {
          return `Fiber must be between ${VALUE_RANGES.FIBER.min} and ${VALUE_RANGES.FIBER.max}`;
        }
        break;
      case 'sugar':
      case 'sugarG':
        if (numValue < VALUE_RANGES.SUGAR.min || numValue > VALUE_RANGES.SUGAR.max) {
          return `Sugar must be between ${VALUE_RANGES.SUGAR.min} and ${VALUE_RANGES.SUGAR.max}`;
        }
        break;
      case 'sodium':
      case 'sodiumMg':
        if (numValue < VALUE_RANGES.SODIUM.min || numValue > VALUE_RANGES.SODIUM.max) {
          return `Sodium must be between ${VALUE_RANGES.SODIUM.min} and ${VALUE_RANGES.SODIUM.max}`;
        }
        break;
    }

    return undefined;
  };

  // Validate all fields
  const validateForm = (): boolean => {
    const newErrors: EditMealFormErrors = {};

    newErrors.mealName = validateField('mealName', formData.mealName);
    newErrors.calories = validateField('calories', formData.nutrition.caloriesPerServing);
    newErrors.protein = validateField('protein', formData.nutrition.proteinG);
    newErrors.carbs = validateField('carbs', formData.nutrition.carbsG);
    newErrors.fat = validateField('fat', formData.nutrition.fatG);

    if (formData.nutrition.fiberG !== undefined) {
      newErrors.fiber = validateField('fiber', formData.nutrition.fiberG);
    }
    if (formData.nutrition.sugarG !== undefined) {
      newErrors.sugar = validateField('sugar', formData.nutrition.sugarG);
    }
    if (formData.nutrition.sodiumMg !== undefined) {
      newErrors.sodium = validateField('sodium', formData.nutrition.sodiumMg);
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== undefined);
  };

  // Handle input change
  const handleChange = (field: string, value: string | number) => {
    if (field === 'mealName') {
      setFormData(prev => ({ ...prev, mealName: String(value) }));

      // Validate immediately if field was touched
      if (touched.mealName) {
        const error = validateField('mealName', String(value));
        setErrors(prev => ({ ...prev, mealName: error }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        nutrition: {
          ...prev.nutrition,
          [field]: value === '' ? undefined : Number(value)
        }
      }));

      // Map display field names for validation
      const fieldMap: Record<string, string> = {
        'caloriesPerServing': 'calories',
        'proteinG': 'protein',
        'carbsG': 'carbs',
        'fatG': 'fat',
        'fiberG': 'fiber',
        'sugarG': 'sugar',
        'sodiumMg': 'sodium',
      };

      const displayField = fieldMap[field] || field;

      // Validate immediately if field was touched
      if (touched[displayField]) {
        const error = validateField(displayField, value === '' ? undefined : Number(value));
        setErrors(prev => ({ ...prev, [displayField]: error }));
      }
    }
  };

  // Handle blur
  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));

    let value: string | number | undefined;
    let validationField = field;

    if (field === 'mealName') {
      value = formData.mealName;
    } else {
      // Map display field names to nutrition object keys
      const fieldMap: Record<string, keyof typeof formData.nutrition> = {
        'calories': 'caloriesPerServing',
        'protein': 'proteinG',
        'carbs': 'carbsG',
        'fat': 'fatG',
        'fiber': 'fiberG',
        'sugar': 'sugarG',
        'sodium': 'sodiumMg',
      };

      const nutritionKey = fieldMap[field] || field;
      value = formData.nutrition[nutritionKey as keyof typeof formData.nutrition];
      validationField = field; // Keep the display field name for error messages
    }

    const error = validateField(validationField, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // Handle close with unsaved changes check
  const handleClose = () => {
    if (hasChanges && !isUpdating) {
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const mealId = meal.id;

      await updateMeal(mealId, formData);

      onSuccess?.();
      onClose();
    } catch (err) {
      // Error is handled by the hook
      console.error('Failed to update meal:', err);
    }
  };

  // Check if form is valid
  const isFormValid = !Object.values(errors).some(error => error !== undefined);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-meal-dialog-title"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
      >
        <div
          style={{
            backgroundColor: isDark ? '#1f2937' : 'white',
            borderRadius: '12px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: isDark
              ? '0 20px 25px -5px rgba(0, 0, 0, 0.4)'
              : '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            border: isDark ? '1px solid #374151' : 'none'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{
            padding: '24px',
            borderBottom: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2
              id="edit-meal-dialog-title"
              style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: isDark ? '#f9fafb' : '#111827',
                margin: 0
              }}
            >
              Edit Meal
            </h2>
            <CloseButton
              onClick={handleClose}
              disabled={isUpdating}
              ariaLabel="Close dialog"
            />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
            {/* Error message from API */}
            {error && (
              <div
                role="alert"
                style={{
                  padding: '12px',
                  marginBottom: '20px',
                  borderRadius: '8px',
                  backgroundColor: isDark ? '#7f1d1d' : '#fee2e2',
                  color: isDark ? '#fca5a5' : '#991b1b',
                  fontSize: '14px'
                }}
              >
                {error.message || 'Failed to update meal'}
              </div>
            )}

            {/* Meal Name */}
            <div style={{ marginBottom: '20px' }}>
              <Input
                ref={firstInputRef}
                id="meal-name"
                label="Meal Name"
                type="text"
                value={formData.mealName}
                onChange={(e) => handleChange('mealName', e.target.value)}
                onBlur={() => handleBlur('mealName')}
                error={touched.mealName ? errors.mealName : undefined}
                state={touched.mealName && errors.mealName ? 'error' : 'default'}
                required
                disabled={isUpdating}
                fullWidth
              />
            </div>

            {/* Nutrition Section */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: isDark ? '#f9fafb' : '#111827',
                marginBottom: '16px'
              }}>
                Nutrition (Per Serving)
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {/* Calories */}
                <Input
                  id="calories"
                  label="Calories"
                  type="number"
                  step="0.1"
                  value={formData.nutrition.caloriesPerServing}
                  onChange={(e) => handleChange('caloriesPerServing', e.target.value)}
                  onBlur={() => handleBlur('calories')}
                  error={touched.calories ? errors.calories : undefined}
                  state={touched.calories && errors.calories ? 'error' : 'default'}
                  required
                  disabled={isUpdating}
                  min={VALUE_RANGES.CALORIES.min}
                  max={VALUE_RANGES.CALORIES.max}
                  fullWidth
                />

                {/* Protein */}
                <Input
                  id="protein"
                  label="Protein (g)"
                  type="number"
                  step="0.1"
                  value={formData.nutrition.proteinG}
                  onChange={(e) => handleChange('proteinG', e.target.value)}
                  onBlur={() => handleBlur('protein')}
                  error={touched.protein ? errors.protein : undefined}
                  state={touched.protein && errors.protein ? 'error' : 'default'}
                  required
                  disabled={isUpdating}
                  min={VALUE_RANGES.PROTEIN.min}
                  max={VALUE_RANGES.PROTEIN.max}
                  fullWidth
                />

                {/* Carbs */}
                <Input
                  id="carbs"
                  label="Carbs (g)"
                  type="number"
                  step="0.1"
                  value={formData.nutrition.carbsG}
                  onChange={(e) => handleChange('carbsG', e.target.value)}
                  onBlur={() => handleBlur('carbs')}
                  error={touched.carbs ? errors.carbs : undefined}
                  state={touched.carbs && errors.carbs ? 'error' : 'default'}
                  required
                  disabled={isUpdating}
                  min={VALUE_RANGES.CARBS.min}
                  max={VALUE_RANGES.CARBS.max}
                  fullWidth
                />

                {/* Fat */}
                <Input
                  id="fat"
                  label="Fat (g)"
                  type="number"
                  step="0.1"
                  value={formData.nutrition.fatG}
                  onChange={(e) => handleChange('fatG', e.target.value)}
                  onBlur={() => handleBlur('fat')}
                  error={touched.fat ? errors.fat : undefined}
                  state={touched.fat && errors.fat ? 'error' : 'default'}
                  required
                  disabled={isUpdating}
                  min={VALUE_RANGES.FAT.min}
                  max={VALUE_RANGES.FAT.max}
                  fullWidth
                />

                {/* Fiber, Sugar, and Sodium fields hidden per requirements */}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={!isFormValid || isUpdating}
                loading={isUpdating}
              >
                {isUpdating ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Unsaved Changes Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showUnsavedDialog}
        onClose={() => setShowUnsavedDialog(false)}
        onConfirm={() => {
          setShowUnsavedDialog(false);
          onClose();
        }}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        cancelText="Keep Editing"
        destructive
      />
    </>,
    document.body
  );
};

export default EditMealDialog;